import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import { PRODUCTS } from "@/lib/products";
import { createOrder, getConfig } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Cart } from "@/components/Cart";
import { ReceiptModal } from "@/components/ReceiptModal";

export default function Kiosk() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState(null);
  const [resetKey, setResetKey] = useState(0);
  const [mobilepayBox, setMobilepayBox] = useState("");

  useEffect(() => {
    getConfig()
      .then((cfg) => setMobilepayBox(cfg.mobilepay_box || ""))
      .catch(() => {});
  }, []);

  const handleAdd = (item) => {
    setItems((prev) => [...prev, item]);
    toast.success(`${item.product_name} tilføjet`);
  };

  const handleRemove = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleCheckout = async () => {
    if (items.length === 0) return;
    setSubmitting(true);
    try {
      const created = await createOrder({
        customer_name: name,
        phone,
        items,
      });
      setOrder(created);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Noget gik galt – prøv igen");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseReceipt = () => {
    setOrder(null);
    setItems([]);
    setName("");
    setPhone("");
  };

  const handleCancelOrder = () => {
    // Cancelled: keep cart + customer info so they can fix their choice
    setOrder(null);
  };

  const handleReset = () => {
    setItems([]);
    setName("");
    setPhone("");
    setOrder(null);
    setResetKey((k) => k + 1);
    toast.success("Nulstillet");
  };

  return (
    <div className="flex flex-col lg:flex-row lg:h-screen w-screen lg:overflow-hidden bg-slate-50">
      {/* Main area */}
      <main className="flex-1 flex flex-col min-w-0 lg:h-full lg:overflow-hidden">
        {/* Header */}
        <header className="px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between shrink-0">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 leading-none">
              ÅF Trøjer
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 font-semibold mt-1">
              Århus Friskole · Vælg din trøje
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="btn-reset"
              onClick={handleReset}
              className="h-10 px-3 sm:px-4 rounded-xl bg-slate-100 text-slate-700 font-bold text-xs sm:text-sm hover:bg-slate-200 active:scale-95 transition-all flex items-center gap-1.5"
            >
              <RotateCcw size={14} strokeWidth={3} />
              Nulstil
            </button>
            <Link
              to="/admin"
              data-testid="link-admin"
              className="text-xs font-bold text-slate-400 hover:text-slate-700 uppercase tracking-wider px-2"
            >
              Admin
            </Link>
          </div>
        </header>

        {/* Products grid */}
        <section className="flex-1 px-4 sm:px-6 lg:px-8 pb-6 min-h-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5 lg:h-full">
            {PRODUCTS.map((p) => (
              <ProductCard key={`${p.id}-${resetKey}`} product={p} onAdd={handleAdd} />
            ))}
          </div>
        </section>
      </main>

      {/* Cart */}
      <Cart
        items={items}
        name={name}
        phone={phone}
        onNameChange={setName}
        onPhoneChange={setPhone}
        onRemove={handleRemove}
        onCheckout={handleCheckout}
        submitting={submitting}
      />

      {order && <ReceiptModal order={order} mobilepayBox={mobilepayBox} onClose={handleCloseReceipt} onCancel={handleCancelOrder} />}
    </div>
  );
}
